import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@omni/ui/components/Button/button.component";
import { Card } from "@omni/ui/components/Card/card";
import { cn } from "@omni/utils/styles";
import { FormProvider } from "react-hook-form";

import { PasswordField } from "@/components/Form/RHF/FormFields/PasswordField/passwordField.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { MessageComponent } from "@/components/General/Messages/messages.component";
import { Spinner } from "@/components/General/Spinner/spinner.component";
import { translateWithoutContext } from "@/redux/modules/value/translations/translations.helpers";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { Config } from "@/utils/config.utils";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";

import {
    ResetPasswordFormValidationSchema,
    ResetPasswordFormValues
} from "./resetPasswordForm.schema";

const FORM_ID = "loginForm";

type ResetPasswordFormProps = {
    buttonText: string;
    onSubmit: (newPassword: string) => Promise<void>;
    isLoading: boolean;
    isPreLoading: boolean;
    hasValidToken: boolean;
};

const ResetPasswordForm = ({
    buttonText,
    onSubmit,
    isLoading,
    isPreLoading,
    hasValidToken
}: ResetPasswordFormProps) => {
    const { t } = useGranite();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            newPassword: "",
            newPasswordRepeat: ""
        },
        resolver: yupResolver(ResetPasswordFormValidationSchema)
    });

    const onFormSubmit = ({ newPassword }: ResetPasswordFormValues) => {
        onSubmit(newPassword);
    };

    return (
        <>
            {isPreLoading && <Spinner />}
            {!isPreLoading && !hasValidToken && (
                <MessageComponent
                    message={{
                        id: "invalid-token",
                        title: translateWithoutContext(
                            "general.form.newPassword.error.title"
                        ),
                        text: translateWithoutContext(
                            "general.form.newPassword.error.message",
                            `<a href="${
                                Config.pages["request-password"]
                            }">${translateWithoutContext(
                                "general.form.newPassword.error.message.click"
                            )}</a>`
                        ),
                        status: "danger",
                        dismissible: false
                    }}
                />
            )}
            {!isPreLoading && hasValidToken && (
                <Card>
                    <div className={cn("checkoutGuest d-md-block")}>
                        <FormInfoProvider
                            formId={FORM_ID}
                            withNeverbounce={false}
                        >
                            {() => (
                                <FormProvider {...methods}>
                                    <form
                                        id={FORM_ID}
                                        onSubmit={methods.handleSubmit(
                                            onFormSubmit
                                        )}
                                    >
                                        <Card.Body>
                                            <Row>
                                                <Col md={12}>
                                                    <PasswordField<ResetPasswordFormValues>
                                                        name="newPassword"
                                                        label={t(
                                                            "general.form.newPassword.label"
                                                        )}
                                                        required
                                                        autoComplete={
                                                            "new-password"
                                                        }
                                                        placeholder={t(
                                                            "general.form.newPassword.placeholder"
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={12}>
                                                    <PasswordField<ResetPasswordFormValues>
                                                        name="newPasswordRepeat"
                                                        label={t(
                                                            "general.form.newPasswordRepeat.label"
                                                        )}
                                                        required
                                                        autoComplete={
                                                            "new-password"
                                                        }
                                                        placeholder={t(
                                                            "general.form.newPasswordRepeat.placeholder"
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                        <Card.Footer
                                            className={cn("border-0 pb-2 px-2")}
                                            style={{
                                                backgroundColor: "inherit"
                                            }}
                                        >
                                            <Button
                                                test="resetPasswordForm-button-submit"
                                                type="submit"
                                                size="lg"
                                                loading={
                                                    methods.formState
                                                        .isSubmitting &&
                                                    isLoading
                                                }
                                            >
                                                {buttonText}
                                            </Button>
                                        </Card.Footer>
                                    </form>
                                </FormProvider>
                            )}
                        </FormInfoProvider>
                    </div>
                </Card>
            )}
        </>
    );
};

export default ResetPasswordForm;
